import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
  ClientOnlineOrdersResponseData,
  ClientOrdersResponseData,
  ClientResponseData,
  ClientsListParams,
  ClientsListResponseData,
  FetchDataParams,
  FetchDepositMovementsType,
} from '@/types/api/queries';

import { HttpClient } from '@/network/HttpClient';

export const CLIENT_QUERY_KEY = 'client';
export const CLIENTS_LIST_QUERY_KEY = 'clients_list';
export const NEW_CLIENTS_LIST_QUERY_KEY = 'new_clients_list';
export const CLIENT_ORDERS_QUERY_KEY = 'client_orders';
export const CLIENT_ONLINE_ORDERS_QUERY_KEY = 'client_online_orders';
export const CLIENT_DEPOSIT_MOVEMENTS_QUERY_KEY = 'client_deposit_movements';
// SS-1260 ledger history. The key above belongs to the legacy deposit logs.
export const CLIENT_DEPOSIT_HISTORY_QUERY_KEY = 'client_deposit_history';

export const useClientQuery = (fetchUrl: string) =>
  useQuery({
    queryKey: [CLIENT_QUERY_KEY, fetchUrl],
    queryFn: () =>
      HttpClient.request<ClientResponseData>({
        method: 'GET',
        url: fetchUrl,
      }),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });

export const useClientsListQuery = (
  url: string,
  queryParams: ClientsListParams['queryParams'],
) =>
  useQuery({
    queryKey: [CLIENTS_LIST_QUERY_KEY, url, queryParams],
    queryFn: () =>
      HttpClient.request<ClientsListResponseData>({
        method: 'GET',
        url,
        params: queryParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useClientOrdersQuery = ({
  fetchUrl,
  paginationParams,
}: FetchDataParams) =>
  useQuery({
    queryKey: [CLIENT_ORDERS_QUERY_KEY, fetchUrl, paginationParams],
    queryFn: () =>
      HttpClient.request<ClientOrdersResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: paginationParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useClientOnlineOrdersQuery = ({
  fetchUrl,
  paginationParams,
}: FetchDataParams) =>
  useQuery({
    queryKey: [CLIENT_ONLINE_ORDERS_QUERY_KEY, fetchUrl, paginationParams],
    queryFn: () =>
      HttpClient.request<ClientOnlineOrdersResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: paginationParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useClientDepositMovementsQuery = ({
  fetchUrl,
  paginationParams,
}: FetchDataParams) =>
  useQuery({
    queryKey: [CLIENT_DEPOSIT_MOVEMENTS_QUERY_KEY, fetchUrl, paginationParams],
    queryFn: () =>
      HttpClient.request<FetchDepositMovementsType>({
        method: 'GET',
        url: fetchUrl,
        params: paginationParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
